import React, { useState } from 'react';
import {
  Container,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { Helmet } from 'react-helmet';
import {
  Card, CardBody, Col, Row,
} from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import {
  investmentStatementQuestion,
  questionList,
} from './QuestionList';
import { companyInformationQuestions } from '../shared/components/CompanyInfo';
import usePendingInvestmentDetail from '../shared/hooks/usePendingInvestmentDetail';
import PartialPageLoader from '../shared/components/Loader/PartialPageLoader';
import Questions from './Questions';
import getStageStatus from '../shared/utils/checkUserPermissions';
import StageStatusMessage from '../shared/components/StageStatusMessage';
import { updatePendingInvestment } from '../shared/services/apiGateway';
import generateRequest from '../shared/services/generateRequest';
import CloseOnConfirmModal from '../shared/components/CloseOnConfirmModal';
import getSearchParams from '../shared/utils/getSearchParams';
import {
  createDigitalAssetQuestions,
  getDigitalAssetGatingQuestions,
} from '../UploadWireInfo/QuestionList';
import {
  CLOSING_DOCS_REVIEW_QUESTION_LABELS,
  createClosingDocsReviewQuestions,
} from '../ClosingDocsReview/QuestionList';

const createQuestionSections = (deal = {}, companyInfoValues = {}) => {
  const savedQuestions = deal.companyInvestmentInformation || {};
  const digitalAssetQuestions = getDigitalAssetGatingQuestions(createDigitalAssetQuestions(
    deal.digitalAssetsQuestions,
  ));
  const closingDocsReviewQuestions = createClosingDocsReviewQuestions(
    deal.closingDocsReviewQuestions,
  );
  const priorInvestmentTermsQuestion = closingDocsReviewQuestions.find((question) => (
    question.label === CLOSING_DOCS_REVIEW_QUESTION_LABELS.priorInvestmentTerms
  ));

  return {
    companyInfo: companyInformationQuestions.map((question) => ({
      ...question,
      value: companyInfoValues[question.name] || question.value,
    })),
    investmentStatement: {
      ...investmentStatementQuestion,
      value: Array.isArray(savedQuestions.investmentStatement?.value)
        ? [...savedQuestions.investmentStatement.value]
        : [],
    },
    digitalAssetQuestions,
    closingDocsReviewQuestions: deal.initialInvestment === false
      ? [priorInvestmentTermsQuestion]
      : [],
    yesNoQuestions: questionList.map((question) => {
      const savedQuestion = savedQuestions.yesNoQuestions?.find((answer) => (
        answer.name === question.name || answer.label === question.label
      ));
      const savedAnswer = savedQuestion?.value;

      return {
        ...question,
        value: typeof savedAnswer === 'boolean' ? savedAnswer : question.value,
      };
    }),
  };
};

const getCompanyInfoAnswer = (companyInfoQuestions, name) => (
  companyInfoQuestions.find((question) => question.name === name)?.value
);

const isCompanyInfoComplete = (companyInfoQuestions) => companyInfoQuestions.every((question) => (
  !!question.value
));

const requiresOoiReviewForQuestions = (questionSections) => {
  const countryIncorp = getCompanyInfoAnswer(questionSections.companyInfo, 'countryIncorp');
  const corpType = getCompanyInfoAnswer(questionSections.companyInfo, 'corpType');

  return (
    (!!countryIncorp && countryIncorp !== 'United States')
    || (!!corpType && corpType !== 'C-Corp')
    || questionSections.digitalAssetQuestions.some((question) => question.value === true)
    || questionSections.closingDocsReviewQuestions.some((question) => question.value === false)
    || questionSections.yesNoQuestions.some((question) => question.value === true)
  );
};

function CompanyInvestmentInformation() {
  const { id } = useParams();
  const closeOnConfirm = getSearchParams('closeoncompletion');
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [stageStatus, setStageStatus] = useState({
    isEditableByRole: false,
  });

  const [questionSections, setQuestionSections] = useState(createQuestionSections);

  const dealData = usePendingInvestmentDetail(id, (deal) => {
    const companyInfoValues = {
      countryIncorp: deal.companyCountryIncorp
        || deal?.hsDealObj?.country
        || null,
      corpType: deal.companyCorpType || null,
      companyTaxStatus: deal.companyTaxStatus || null,
    };

    setQuestionSections(createQuestionSections(deal, companyInfoValues));
    setStageStatus({
      ...getStageStatus(deal),
    });
  });
  const companyName = dealData?.companyName || dealData?.hsDealObj?.company?.name || '';
  const canEditStep = stageStatus.isEditableByRole && !stageStatus.isCompleted;
  const isFormComplete = isCompanyInfoComplete(questionSections.companyInfo)
    && questionSections.investmentStatement.value.length > 0
    && questionSections.digitalAssetQuestions.every((question) => (
      question.value !== null && question.value !== undefined
    ))
    && questionSections.closingDocsReviewQuestions.every((question) => (
      typeof question.value === 'boolean'
      && (question.value !== false || !!question.exp?.trim())
    ))
    && questionSections.yesNoQuestions.every((question) => (
      question.value !== null && question.value !== undefined
    ));
  const questionAnswersRequireOoiReview = requiresOoiReviewForQuestions(questionSections);
  const ooiApprovalReachedReadyPoint = !!dealData?.stagesCompleted?.closingDocsReview;
  const ooiApprovalWasRequired = dealData?.companyInvestmentInformation?.requiresOoiReview === true;
  const requiresOoiReview = questionAnswersRequireOoiReview
    || (ooiApprovalWasRequired
      && !!dealData?.stagesCompleted?.companyInvestmentInformation
      && ooiApprovalReachedReadyPoint);

  const saveCompanyInvestmentInformation = async (completeStep = false) => {
    if (!dealData || !canEditStep || (completeStep && !isFormComplete)) {
      return;
    }

    setIsSubmitting(completeStep);
    setIsSaving(!completeStep);
    const companyInfoAnswers = questionSections.companyInfo.reduce((answers, question) => ({
      ...answers,
      [question.name]: question.value,
    }), {});
    const stagesCompleted = { ...(dealData.stagesCompleted || {}) };
    delete stagesCompleted.companyInvestmentInformation;
    delete stagesCompleted.ooiApproval;
    if (completeStep) {
      stagesCompleted.companyInvestmentInformation = new Date().toISOString();
    }
    const digitalAssetsQuestions = createDigitalAssetQuestions([
      ...questionSections.digitalAssetQuestions,
      ...(dealData.digitalAssetsQuestions || []),
    ]).filter((question) => question.value !== null && question.value !== '');
    const closingDocsReviewQuestions = createClosingDocsReviewQuestions([
      ...questionSections.closingDocsReviewQuestions,
      ...(dealData.closingDocsReviewQuestions || []),
    ]);
    const updatedDealData = {
      ...dealData,
      companyCountryIncorp: companyInfoAnswers.countryIncorp,
      companyCorpType: companyInfoAnswers.corpType,
      companyTaxStatus: companyInfoAnswers.companyTaxStatus,
      digitalAssetsQuestions,
      closingDocsReviewQuestions,
      companyInvestmentInformation: {
        ...dealData.companyInvestmentInformation,
        investmentStatement: questionSections.investmentStatement,
        yesNoQuestions: questionSections.yesNoQuestions,
        requiresOoiReview,
      },
      stagesCompleted,
    };

    try {
      await updatePendingInvestment(generateRequest(updatedDealData));
      if (!completeStep) {
        return;
      }
      if (closeOnConfirm) {
        setCompletionModalOpen(true);
      } else {
        history.push(`/deal-stages/${id}`);
      }
    } finally {
      setIsSubmitting(false);
      setIsSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Company Investment Information</title>
      </Helmet>
      <Container className="med-width-form">
        <Card>
          {!dealData && PartialPageLoader()}
          {dealData && (
            <CardBody>
              <Row>
                <Col>
                  <h3>Company/Investment Information</h3>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Questions
                    companyName={companyName}
                    questionSections={questionSections}
                    setQuestionSections={setQuestionSections}
                    disabled={!canEditStep}
                  />
                </Col>
              </Row>
              {canEditStep && (
                <Row>
                  <Col className="mt-5 d-flex justify-content-between w-100">
                    <LoadingButton
                      variant="outlined"
                      color="secondary"
                      disabled={isSubmitting || isSaving}
                      loading={isSaving}
                      onClick={() => saveCompanyInvestmentInformation()}
                    >
                      Save
                    </LoadingButton>
                    <LoadingButton
                      variant="contained"
                      color="secondary"
                      disabled={isSubmitting || isSaving || !isFormComplete}
                      loading={isSubmitting}
                      onClick={() => saveCompanyInvestmentInformation(true)}
                    >
                      Submit
                    </LoadingButton>
                  </Col>
                </Row>
              )}
              <StageStatusMessage stageStatus={stageStatus} />
            </CardBody>
          )}
        </Card>
      </Container>
      <CloseOnConfirmModal modalOpen={completionModalOpen} />
    </>
  );
}

export default CompanyInvestmentInformation;
